import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { DEFAULT_REPORT_FILTERS } from "@/lib/reportDefaults";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ReportExport } from "@/components/reports/ReportExport";
import { formatCurrency, cn } from "@/lib/utils";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  Banknote,
  Building2,
  Smartphone,
  PiggyBank,
  CreditCard,
  AlertTriangle,
  Receipt,
  RefreshCw,
  BarChart3,
  ArrowDownLeft,
  ArrowUpRight,
} from "lucide-react";

interface FinancialData {
  summary: {
    openingBalance: number;
    totalInflows: number;
    totalOutflows: number;
    closingBalance: number;
  };
  paymentMethods: {
    cash: number;
    bankTransfer: number;
    mobileMoney: number;
  };
  inflows: {
    savingsCollected: number;
    loanRepayments: number;
    penalties: number;
  };
  outflows: {
    expenses: number;
  };
  recentTransactions: Array<{
    id: string;
    date: string;
    description: string;
    type: "inflow" | "outflow";
    amount: number;
    method: string;
  }>;
}

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    red: "bg-red-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    red: "text-red-600",
    blue: "text-blue-600",
    amber: "text-amber-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {typeof value === "number" ? formatCurrency(value) : value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function BreakdownCard({
  title,
  items,
  loading = false,
}: {
  title: string;
  items: Array<{ label: string; value: number; icon: React.ReactNode; color: string }>;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold mb-3">{title}</h3>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn("flex h-7 w-7 items-center justify-center rounded-md", item.color)}>
                    {item.icon}
                  </div>
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(item.value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function FinancialReport() {
  const [filters, setFilters] = useState<Record<string, any>>(DEFAULT_REPORT_FILTERS);

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["reports-financial", filters],
    async () => {
      const params: Record<string, string> = {};
      if (filters.dateStart) params.dateStart = filters.dateStart;
      if (filters.dateEnd) params.dateEnd = filters.dateEnd;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.paymentMethod && filters.paymentMethod !== "all") params.paymentMethod = filters.paymentMethod;

      const { data } = await api.get("/reports/financial", { params });
      return data?.data as FinancialData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const loading = status === "loading";
  const summary = rawData?.summary || { openingBalance: 0, totalInflows: 0, totalOutflows: 0, closingBalance: 0 };
  const paymentMethods = rawData?.paymentMethods || { cash: 0, bankTransfer: 0, mobileMoney: 0 };
  const inflows = rawData?.inflows || { savingsCollected: 0, loanRepayments: 0, penalties: 0 };
  const outflows = rawData?.outflows || { expenses: 0 };
  const transactions = rawData?.recentTransactions || [];

  const exportColumns = [
    { key: "date", label: "Date" },
    { key: "description", label: "Description" },
    { key: "type", label: "Type" },
    { key: "amount", label: "Amount" },
    { key: "method", label: "Method" },
  ];

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Financial Report
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Comprehensive financial overview and transaction history
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load financial report
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Financial Report
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      Comprehensive financial overview and transaction history
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          show={["dateStart", "dateEnd", "month", "year", "paymentMethod"]}
        />

        {/* Summary Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Opening Balance"
            value={summary.openingBalance}
            icon={<Wallet className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Total Inflows"
            value={summary.totalInflows}
            icon={<TrendingUp className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Total Outflows"
            value={summary.totalOutflows}
            icon={<TrendingDown className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
          <SummaryCard
            title="Closing Balance"
            value={summary.closingBalance}
            icon={<DollarSign className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
        </div>

        {/* Payment Method Breakdown */}
        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryCard
            title="Cash"
            value={paymentMethods.cash}
            icon={<Banknote className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Bank Transfer"
            value={paymentMethods.bankTransfer}
            icon={<Building2 className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Mobile Money"
            value={paymentMethods.mobileMoney}
            icon={<Smartphone className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
        </div>

        {/* Inflows & Outflows Breakdown */}
        <div className="grid gap-4 sm:grid-cols-2">
          <BreakdownCard
            title="Inflows Breakdown"
            loading={loading}
            items={[
              {
                label: "Savings Collected",
                value: inflows.savingsCollected,
                icon: <PiggyBank className="h-3.5 w-3.5 text-[#009A44]" />,
                color: "bg-[#009A44]/10",
              },
              {
                label: "Loan Repayments",
                value: inflows.loanRepayments,
                icon: <CreditCard className="h-3.5 w-3.5 text-blue-600" />,
                color: "bg-blue-50",
              },
              {
                label: "Penalties",
                value: inflows.penalties,
                icon: <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />,
                color: "bg-amber-50",
              },
            ]}
          />
          <BreakdownCard
            title="Outflows Breakdown"
            loading={loading}
            items={[
              {
                label: "Expenses",
                value: outflows.expenses,
                icon: <Receipt className="h-3.5 w-3.5 text-red-600" />,
                color: "bg-red-50",
              },
            ]}
          />
        </div>

        {/* Recent Transactions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold">Recent Transactions</h3>
              <ReportExport
                data={transactions}
                filename="financial-report"
                title="Financial Report"
                columns={exportColumns}
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="py-12 text-center">
                <DollarSign className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">Date</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Description</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Type</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Amount</th>
                      <th className="pb-2 font-medium text-gray-500">Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                        <td className="py-3 pr-4 text-gray-600">
                          {new Date(tx.date).toLocaleDateString()}
                        </td>
                        <td className="py-3 pr-4 font-medium text-gray-900">{tx.description}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                              tx.type === "inflow"
                                ? "bg-[#009A44]/10 text-[#009A44]"
                                : "bg-red-50 text-red-600"
                            )}
                          >
                            {tx.type === "inflow" ? (
                              <ArrowDownLeft className="h-3 w-3" />
                            ) : (
                              <ArrowUpRight className="h-3 w-3" />
                            )}
                            {tx.type === "inflow" ? "Inflow" : "Outflow"}
                          </span>
                        </td>
                        <td
                          className={cn(
                            "py-3 pr-4 text-right font-semibold",
                            tx.type === "inflow" ? "text-[#009A44]" : "text-red-600"
                          )}
                        >
                          {tx.type === "inflow" ? "+" : "-"}
                          {formatCurrency(tx.amount)}
                        </td>
                        <td className="py-3 text-gray-600 capitalize">
                          {tx.method.replace("_", " ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
